import type { GetContractsResponse } from '@vy/api-definition';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { findIntegrationByProviderInEntities, formatPrice } from '@vy/services';
import { DEFAULT_CURRENCY, INTEGRATION_PROVIDERS, type Plan, SYNC_STATUSES } from '@vy/types';
import { conditionalWithDefault, isEmptyArray, isNotNullOrUndefined, vyDate, type ItemOf } from '@vy/utils';

import { calculateEstimatedPlanValue } from '../../../../utils/contract/calculateEstimatedPlanValue';
import { formatDate } from '../../../../utils/date';
import { sanitizeCsvString } from '../../../../utils/string';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { CONTRACTS_COLUMNS_KEYS } from '../plans-page-consts/contracts-columns';
import { CONTRACTS_PAGE_CSV_HEADERS } from './contracts-page-csv-headers';
import { getPlanDurationLabel } from '../../utils';

type ContractOverviewRow = ItemOf<GetContractsResponse['contracts']>;

type CSVFieldOrder = {
  field: string;
  header: string;
  getValue: (contract: ContractOverviewRow) => string;
};

const CSV_FIELD_ORDER: CSVFieldOrder[] = [
  {
    field: CONTRACTS_COLUMNS_KEYS.STATUS,
    header: CONTRACTS_PAGE_CSV_HEADERS.STATUS,
    getValue: (contract) => contract.status,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME,
    header: CONTRACTS_PAGE_CSV_HEADERS.CUSTOMER,
    getValue: (contract) => sanitizeCsvString(contract.customer.name),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CUSTOMER_ID,
    header: CONTRACTS_PAGE_CSV_HEADERS.CUSTOMER_ID,
    getValue: (contract) => contract.customer.id,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_NAME,
    header: CONTRACTS_PAGE_CSV_HEADERS.CONTRACT,
    getValue: (contract) => sanitizeCsvString(contract.plan.name),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.START_DATE,
    header: CONTRACTS_PAGE_CSV_HEADERS.START_DATE,
    getValue: (contract) => formatDate(getContractStartDate(contract), 'MM-DD-YYYY'),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.END_DATE,
    header: CONTRACTS_PAGE_CSV_HEADERS.END_DATE,
    getValue: (contract) => formatOptionalDate(contract.endDate),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CREATED_AT,
    header: 'Created At',
    getValue: (contract) => formatOptionalDate(contract.createdAt),
  },
  {
    field: 'updatedAt',
    header: 'Updated At',
    getValue: (contract) => formatOptionalDate(contract.updatedAt),
  },
  {
    field: 'daysToEnd',
    header: CONTRACTS_PAGE_CSV_HEADERS.DAYS_TO_END,
    getValue: (contract) => getDaysToEnd(contract),
  },
  {
    field: 'contractEstimation',
    header: CONTRACTS_PAGE_CSV_HEADERS.ESTIMATION,
    getValue: (contract) => formatContractEstimation(contract),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.SIGNATURE_DATE,
    header: CONTRACTS_PAGE_CSV_HEADERS.SIGNATURE_DATE,
    getValue: (contract) => formatOptionalDate(contract.signatureDate),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.BILLING_INTERVAL,
    header: CONTRACTS_PAGE_CSV_HEADERS.BILLING_INTERVAL,
    getValue: (contract) => contract.plan.billingData.billingInterval,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_DURATION,
    header: CONTRACTS_PAGE_CSV_HEADERS.DURATION,
    getValue: (contract) => getPlanDurationLabel(contract.plan.billingData.duration),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.AUTO_RENEWAL,
    header: CONTRACTS_PAGE_CSV_HEADERS.AUTO_RENEWAL,
    getValue: (contract) => (contract.plan.billingData.autoRenewal ? 'Yes' : 'No'),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.SYNC_STATUS,
    header: CONTRACTS_PAGE_CSV_HEADERS.SYNC_STATUS,
    getValue: (contract) => getSyncStatusLabel(contract),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CURRENCY,
    header: CONTRACTS_PAGE_CSV_HEADERS.CURRENCY,
    getValue: (contract) => contract.customer.settings?.currency ?? DEFAULT_CURRENCY,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.ID,
    header: CONTRACTS_PAGE_CSV_HEADERS.ID,
    getValue: (contract) => contract.id,
  },
];

export function generateCSV(contracts: ContractOverviewRow[], visibleFields?: string[]) {
  const customFieldNames = extractUniqueCustomFieldNames(
    contracts.map((contract) => contract.customFieldValues),
  );
  const fieldOrder = [...CSV_FIELD_ORDER, ...buildCustomFieldOrder(customFieldNames)];
  const fieldsToExport = getFieldsToExport(visibleFields, fieldOrder);
  const headers = generateCSVHeaders(fieldsToExport, fieldOrder);
  const rows = generateCSVRows(contracts, fieldsToExport, fieldOrder);

  return `${headers.join(',')}\n${rows.join('\n')}`;
}

function getFieldsToExport(visibleFields: string[] | undefined, fieldOrder: CSVFieldOrder[]) {
  if (!isNotNullOrUndefined(visibleFields) || isEmptyArray(visibleFields)) {
    return fieldOrder.map((item) => item.field);
  }

  const fields = fieldOrder.filter((item) => visibleFields.includes(item.field)).map(
    (item) => item.field,
  );

  if (isEmptyArray(fields)) {
    return fieldOrder.map((item) => item.field);
  }

  return fields;
}

function generateCSVHeaders(fieldsToExport: string[], fieldOrder: CSVFieldOrder[]) {
  return fieldOrder.filter((item) => fieldsToExport.includes(item.field)).map((item) => item.header);
}

function generateCSVRows(
  contracts: ContractOverviewRow[],
  fieldsToExport: string[],
  fieldOrder: CSVFieldOrder[],
) {
  return contracts.map((contract) => {
    const values = fieldOrder.filter((item) => fieldsToExport.includes(item.field)).map((item) =>
      item.getValue(contract),
    );
    return values.join(',');
  });
}

function getContractStartDate(contract: ContractOverviewRow) {
  return conditionalWithDefault(contract.initialStartDate, contract.startDate, () =>
    isNotNullOrUndefined(contract.revisedAt),
  );
}

function formatOptionalDate(date: Date | string | null | undefined) {
  if (!isNotNullOrUndefined(date)) {
    return '-';
  }

  return formatDate(vyDate(date), 'MM-DD-YYYY');
}

function getDaysToEnd(contract: ContractOverviewRow) {
  if (!isNotNullOrUndefined(contract.endDate)) {
    return '-';
  }

  const today = vyDate();
  const endDate = vyDate(contract.endDate);

  return String(endDate.diff(today, 'days'));
}

function formatContractEstimation(contract: ContractOverviewRow) {
  const estimatedPrice = calculateEstimatedPlanValue(contract.plan as Plan);
  const currency = contract.customer.settings?.currency ?? DEFAULT_CURRENCY;
  const formattedPrice = formatPrice(estimatedPrice, currency);

  return `"${formattedPrice}"`;
}

function getSyncStatusLabel(contract: ContractOverviewRow) {
  const netSuiteIntegrationEntity = findIntegrationByProviderInEntities(
    contract.integrationEntities ?? [],
    INTEGRATION_PROVIDERS.netSuite,
  );

  return netSuiteIntegrationEntity?.syncStatus ?? SYNC_STATUSES.None;
}

function buildCustomFieldOrder(fieldNames: string[]): CSVFieldOrder[] {
  return fieldNames.map((fieldName) => ({
    field: fieldName,
    header: fieldName,
    getValue: (contract) => getCustomFieldValue(contract.customFieldValues, fieldName),
  }));
}

function getCustomFieldValue(customFieldValues: CustomFieldValue[] | null | undefined, fieldName: string) {
  if (!customFieldValues) {
    return '-';
  }

  const customField = customFieldValues.find((field) => field.fieldName === fieldName);
  if (!customField || customField.value === null || customField.value === undefined) {
    return '-';
  }

  return sanitizeCsvString(String(customField.value));
}
